import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import type { TimelineState } from '@xzdarcy/react-timeline-editor';

import { EditorTimeline, VideoClip } from 'services/VideoEditingService';
import { STAGE_MAX_HEIGHT } from '../layout';
import { videoLayout, timelineDuration, overlayClips, overlayCoverAt } from '../timelineModel';

// The composed player for the multi-track timeline. It owns the real media
// elements and plays the edit as one continuous piece:
//   • Video track — two <video> buffers (A/B). The active one plays the current
//     clip (seeked to its source in-point + elapsed); the idle one preloads the
//     next clip so the boundary swap is near-seamless (separate source MP4s can't
//     be frame-perfect — fine for a preview/instruction tool, we don't render).
//   • Audio layers — one <audio> per clip, seeked/played at its timeline position.
// A rAF loop advances the playhead, drives the media, and pushes the time onto the
// timeline's cursor so both stay in sync. It ticks on wall-clock time, but whenever
// the active video element is genuinely playing its OWN position wins (`mediaClock`)
// — the picture on screen is the truth. That matters because the media pipeline keeps
// playing when the browser stops delivering animation frames (backgrounded/occluded
// window, low-power mode, a busy machine); a pure wall clock would freeze there and
// stay behind the picture for the rest of the session.
// The library timeline is used only for the UI/interaction (we don't use its engine
// playback) — this controller is the single source of playback truth.

export interface TimelinePreviewHandle {
  play: () => void;
  pause: () => void;
  seek: (t: number) => void;
  isPlaying: () => boolean;
}

interface Props {
  timeline: EditorTimeline;
  clipsById: Map<string, VideoClip>;
  // The library timeline ref, so playback can move its cursor in lock-step.
  timelineStateRef: React.MutableRefObject<TimelineState | null>;
  // Optional preview-only in/out points. Playback loops inside this range; the
  // stored timeline and its clip trims are never changed.
  playbackRange?: { startSec: number; endSec: number };
  onPlayStateChange?: (playing: boolean) => void;
  onTimeChange?: (t: number) => void;
  onPrimaryVideoCanPlayChange?: (canPlay: boolean) => void;
}

const DRIFT_TOLERANCE = 0.35; // seconds a media element may drift before we re-seek

const TimelinePreview = forwardRef<TimelinePreviewHandle, Props>(function TimelinePreview(
  { timeline, clipsById, timelineStateRef, playbackRange, onPlayStateChange, onTimeChange, onPrimaryVideoCanPlayChange }, ref,
) {
  const vidA = useRef<HTMLVideoElement>(null);
  const vidB = useRef<HTMLVideoElement>(null);
  // B-roll cutaway element: always muted, layered above the A/B pair; where an
  // overlay clip covers the timeline its picture replaces the main track's while
  // the main clip's audio keeps playing underneath. Single element (no preload
  // double-buffering) — cutaways are sparse, and the drift re-seek recovers.
  const vidOverlay = useRef<HTMLVideoElement>(null);
  const overlayElClipId = useRef('');
  // Which clip id is currently loaded in each buffer, and which buffer is showing.
  const elClipId = useRef<[string, string]>(['', '']);
  const activeIdx = useRef<0 | 1>(0);
  const videoCanPlay = useRef<[boolean, boolean]>([false, false]);
  const reportedPrimaryCanPlay = useRef(false);
  const primaryCanPlayCallback = useRef(onPrimaryVideoCanPlayChange);
  primaryCanPlayCallback.current = onPrimaryVideoCanPlayChange;
  // Audio elements by clip id (populated by ref callbacks below).
  const audioEls = useRef<Map<string, HTMLAudioElement>>(new Map());

  // Latest props for the rAF loop to read without stale closures.
  const dataRef = useRef({ timeline, clipsById, playbackRange });
  dataRef.current = { timeline, clipsById, playbackRange };

  const playingRef = useRef(false);
  // Mirror the playing state into React so the on-video play/pause button shows
  // the right icon (the ref alone doesn't re-render).
  const [uiPlaying, setUiPlaying] = useState(false);
  const setPlayState = (playing: boolean) => { setUiPlaying(playing); onPlayStateChange?.(playing); };
  const playheadRef = useRef(0);
  const rafRef = useRef<number | undefined>(undefined);
  const startWallRef = useRef(0);
  const startHeadRef = useRef(0);

  function playbackBounds() {
    const { timeline: tl, playbackRange: requested } = dataRef.current;
    const total = timelineDuration(tl);
    const start = Math.max(0, Math.min(requested?.startSec ?? 0, total));
    const end = Math.max(start, Math.min(requested?.endSec ?? total, total));
    return { total, start, end };
  }

  const elAt = (idx: 0 | 1) => (idx === 0 ? vidA.current : vidB.current);

  function reportPrimaryCanPlay(canPlay: boolean) {
    if (reportedPrimaryCanPlay.current === canPlay) return;
    reportedPrimaryCanPlay.current = canPlay;
    primaryCanPlayCallback.current?.(canPlay);
  }

  function loadVideoBuffer(idx: 0 | 1, clipId: string, src: string) {
    const el = elAt(idx);
    if (!el) return;
    videoCanPlay.current[idx] = false;
    el.src = src;
    el.load();
    elClipId.current[idx] = clipId;
  }

  function handleVideoCanPlay(idx: 0 | 1) {
    videoCanPlay.current[idx] = true;
    if (activeIdx.current === idx && elClipId.current[idx]) reportPrimaryCanPlay(true);
  }

  // Timeline time implied by the picture on screen: the active buffer's own
  // position mapped back through its slot in the layout. Null whenever the
  // element can't be trusted as a clock — loading, mid-seek, paused, or already
  // playing past its trimmed out-point (the hand-off to the next clip is the
  // wall clock's job).
  function mediaClock(): number | null {
    const idx = activeIdx.current;
    const el = elAt(idx);
    const clipId = elClipId.current[idx];
    if (!el || !clipId || el.paused || el.seeking || el.readyState < 2) return null;
    const laid = videoLayout(dataRef.current.timeline).find((l) => l.clip.id === clipId);
    if (!laid) return null;
    const t = laid.start + (el.currentTime - laid.clip.sourceInSec);
    if (t < laid.start - 0.5 || t > laid.end + 0.5) return null;
    return t;
  }

  // Re-anchor the wall clock so it carries on from `t` while no media clock is
  // available (a gap between clips, an element still loading).
  function anchorClock(t: number) {
    startWallRef.current = performance.now();
    startHeadRef.current = t;
  }

  // timeupdate still fires (~4×/s) when animation frames don't, so it keeps the
  // reported playhead honest while the loop is stalled. It only reports — the
  // composition itself (clip switching, audio/overlay sync) needs the rAF loop.
  function handleVideoTimeUpdate(idx: 0 | 1) {
    if (!playingRef.current || activeIdx.current !== idx) return;
    const media = mediaClock();
    if (media == null) return;
    const { start, end } = playbackBounds();
    if (media < start || media > end) return;
    playheadRef.current = media;
    anchorClock(media);
    report(media);
  }

  function syncVideo(el: HTMLVideoElement, target: number, playing: boolean) {
    if (playing) {
      if (Math.abs(el.currentTime - target) > DRIFT_TOLERANCE) { try { el.currentTime = target; } catch { /* not seekable yet */ } }
      if (el.paused) el.play().catch(() => { /* autoplay/seek race — corrected next frame */ });
    } else {
      try { el.currentTime = target; } catch { /* not seekable yet */ }
      if (!el.paused) el.pause();
    }
  }

  function hideVideos() {
    [vidA.current, vidB.current].forEach((el) => { if (el) { el.style.opacity = '0'; if (!el.paused) el.pause(); } });
  }

  // Position/seek/play the video buffers and audio elements for timeline time `t`.
  function renderAt(t: number, playing: boolean) {
    const { timeline: tl, clipsById: byId } = dataRef.current;

    // ── Video track ──
    const layout = videoLayout(tl);
    let active = layout.find((l) => t >= l.start && t < l.end) || null;
    if (!active && layout.length && t >= layout[layout.length - 1].end) active = layout[layout.length - 1];

    if (!active) {
      hideVideos();
      reportPrimaryCanPlay(false);
    } else {
      const src = byId.get(active.clip.sourceFileId)?.streamUrl;
      if (!src) {
        hideVideos();
        reportPrimaryCanPlay(false);
      } else {
        // Find (or load) the buffer holding this clip.
        let idx: 0 | 1 | -1 = elClipId.current[0] === active.clip.id ? 0 : elClipId.current[1] === active.clip.id ? 1 : -1;
        if (idx === -1) {
          idx = activeIdx.current === 0 ? 1 : 0;
          loadVideoBuffer(idx, active.clip.id, src);
        }
        activeIdx.current = idx;
        reportPrimaryCanPlay(videoCanPlay.current[idx]);
        const el = elAt(idx);
        if (el) {
          el.style.opacity = '1';
          el.muted = !!active.clip.muteAudio;
          syncVideo(el, active.clip.sourceInSec + (t - active.start), playing);
        }
        // Hide/pause the other buffer, then use it to preload the next clip.
        const otherIdx = (idx === 0 ? 1 : 0) as 0 | 1;
        const other = elAt(otherIdx);
        if (other) { other.style.opacity = '0'; if (!other.paused) other.pause(); }
        const next = layout[active.index + 1];
        if (next && other && elClipId.current[otherIdx] !== next.clip.id) {
          const nextUrl = byId.get(next.clip.sourceFileId)?.streamUrl;
          if (nextUrl) {
            loadVideoBuffer(otherIdx, next.clip.id, nextUrl);
            other.muted = true;
            try { other.currentTime = next.clip.sourceInSec; } catch { /* not seekable yet */ }
          }
        }
      }
    }

    // ── B-roll overlay track ── (independent of the main-video block above, so
    // a cutaway still shows when it extends past the main track's end)
    const overlayEl = vidOverlay.current;
    if (overlayEl) {
      const cover = overlayCoverAt(tl, t);
      const coverSrc = cover ? byId.get(cover.clip.sourceFileId)?.streamUrl : undefined;
      if (!cover || !coverSrc) {
        overlayEl.style.opacity = '0';
        if (!overlayEl.paused) overlayEl.pause();
      } else {
        if (overlayElClipId.current !== cover.clip.id) {
          overlayEl.src = coverSrc;
          overlayEl.load();
          overlayElClipId.current = cover.clip.id;
        }
        overlayEl.muted = true; // never contributes audio — the main clip's sound continues
        overlayEl.style.opacity = '1';
        syncVideo(overlayEl, cover.clip.sourceInSec + (t - cover.start), playing);
      }
    }

    // ── Audio layers ──
    for (const track of tl.audioTracks || []) {
      for (const c of track.clips || []) {
        const el = audioEls.current.get(c.id);
        if (!el) continue;
        const dur = Math.max(0, (c.sourceOutSec || 0) - (c.sourceInSec || 0));
        const start = c.timelineStartSec || 0;
        const inRange = t >= start && t < start + dur;
        if (inRange) {
          const target = c.sourceInSec + (t - start);
          if (playing) {
            if (Math.abs(el.currentTime - target) > DRIFT_TOLERANCE) { try { el.currentTime = target; } catch { /* noop */ } }
            if (el.paused) el.play().catch(() => { /* corrected next frame */ });
          } else {
            try { el.currentTime = target; } catch { /* noop */ }
            if (!el.paused) el.pause();
          }
        } else if (!el.paused) {
          el.pause();
        }
      }
    }
  }

  function report(t: number) {
    try { timelineStateRef.current?.setTime(t); } catch { /* library not mounted yet */ }
    onTimeChange?.(t);
  }

  function stopLoop() {
    if (rafRef.current != null) { cancelAnimationFrame(rafRef.current); rafRef.current = undefined; }
    playingRef.current = false;
    setPlayState(false);
  }

  function tick() {
    const now = performance.now();
    const { total, start, end } = playbackBounds();
    let t = startHeadRef.current + (now - startWallRef.current) / 1000;
    // The playing picture outranks the wall clock — see mediaClock(). Re-anchoring
    // keeps the wall clock continuous for the frames where no media clock exists.
    const media = mediaClock();
    if (media != null) { t = media; anchorClock(t); }
    if (total <= 0) { playheadRef.current = 0; renderAt(0, false); report(0); stopLoop(); return; }
    if (end <= start) {
      playheadRef.current = start;
      renderAt(start, false);
      report(start);
      stopLoop();
      return;
    }
    if (t < start || t >= end) {
      // Reaching the selected out-point loops the composed preview, mirroring
      // the iPhone-style source-clip selector above the editor.
      t = start;
      playheadRef.current = t;
      startWallRef.current = now;
      startHeadRef.current = t;
      renderAt(t, true);
      report(t);
      rafRef.current = requestAnimationFrame(tick);
      return;
    }
    playheadRef.current = t;
    renderAt(t, true);
    report(t);
    rafRef.current = requestAnimationFrame(tick);
  }

  function play() {
    if (playingRef.current) return;
    const { total, start, end } = playbackBounds();
    if (total <= 0 || end <= start) return;
    if (playheadRef.current < start || playheadRef.current >= end) playheadRef.current = start;
    startWallRef.current = performance.now();
    startHeadRef.current = playheadRef.current;
    playingRef.current = true;
    setPlayState(true);
    rafRef.current = requestAnimationFrame(tick);
  }

  // Click the video (or its play/pause button) to toggle playback.
  function togglePlay() {
    if (playingRef.current) pause();
    else play();
  }

  function pause() {
    if (!playingRef.current) return;
    // Stop where the viewer actually is, not where a stalled loop last left the
    // playhead — otherwise pausing yanks the picture backwards.
    const media = mediaClock();
    if (media != null) {
      const { start, end } = playbackBounds();
      playheadRef.current = Math.max(start, Math.min(media, end));
    }
    stopLoop();
    renderAt(playheadRef.current, false);
    report(playheadRef.current);
  }

  function seek(t: number) {
    const total = timelineDuration(dataRef.current.timeline);
    const clamped = Math.max(0, Math.min(t, total || 0));
    playheadRef.current = clamped;
    if (playingRef.current) anchorClock(clamped);
    // Publish before touching the media: a seek that a media element rejects must
    // still leave the scrubber where the caller put it.
    report(clamped);
    renderAt(clamped, playingRef.current);
  }

  useImperativeHandle(ref, () => ({
    play,
    pause,
    seek,
    isPlaying: () => playingRef.current,
  }), []); // eslint-disable-line react-hooks/exhaustive-deps

  // Refresh the shown frame when the timeline/clips/range change while paused
  // (e.g. a clip was trimmed or a preview handle moved).
  useEffect(() => {
    if (!playingRef.current) {
      const { start, end } = playbackBounds();
      const clamped = Math.max(start, Math.min(playheadRef.current, end));
      playheadRef.current = clamped;
      renderAt(clamped, false);
      report(clamped);
    }
  }, [timeline, clipsById, playbackRange?.startSec, playbackRange?.endSec]); // eslint-disable-line react-hooks/exhaustive-deps

  // Stop the loop + all media on unmount.
  useEffect(() => () => {
    stopLoop();
    [vidA.current, vidB.current, vidOverlay.current].forEach((el) => { if (el && !el.paused) el.pause(); });
    audioEls.current.forEach((el) => { if (!el.paused) el.pause(); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const hasVideo = (timeline.videoTrack.clips || []).length > 0 || overlayClips(timeline).length > 0;

  // Size the stage to the footage's own aspect so a portrait clip gets a snug,
  // centered frame instead of a wide 16:9 box with dead space. Height is fixed
  // (STAGE_MAX_HEIGHT); width follows the first video clip's aspect ratio.
  const firstVideoSource = (() => {
    const firstMain = (timeline.videoTrack.clips || [])[0];
    const firstOverlay = overlayClips(timeline)[0];
    const src = firstMain ? clipsById.get(firstMain.sourceFileId) : firstOverlay ? clipsById.get(firstOverlay.sourceFileId) : undefined;
    return src;
  })();
  const aspect = firstVideoSource?.width && firstVideoSource?.height
    ? `${firstVideoSource.width} / ${firstVideoSource.height}`
    : '9 / 16';

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'flex-start', justifyContent: 'center' }}>
      <Box sx={{
        position: 'relative', height: '100%', minHeight: STAGE_MAX_HEIGHT, aspectRatio: aspect, maxWidth: '100%',
        backgroundColor: '#fff', borderRadius: 1, overflow: 'hidden',
        border: '1px solid', borderColor: 'divider',
        // Reveal the play/pause control on hover (it also shows while paused).
        '&:hover .preview-play-toggle': { opacity: 1 },
      }}>
        {[vidA, vidB].map((r, i) => (
          <Box
            key={i}
            component="video"
            ref={r}
            playsInline
            preload="auto"
            onCanPlay={() => handleVideoCanPlay(i as 0 | 1)}
            onTimeUpdate={() => handleVideoTimeUpdate(i as 0 | 1)}
            sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', opacity: 0, backgroundColor: '#fff' }}
          />
        ))}
        {/* B-roll cutaway layer — after the A/B pair so DOM order stacks it on top. */}
        <Box
          component="video"
          data-testid="preview-overlay-video"
          ref={vidOverlay}
          muted
          playsInline
          preload="auto"
          sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', opacity: 0, backgroundColor: 'transparent' }}
        />
        {/* Click anywhere on the video to play/pause; the button shows on hover
            (and stays visible while paused). */}
        {hasVideo && (
          <Box
            className="preview-play-toggle"
            onClick={togglePlay}
            sx={{
              position: 'absolute', inset: 0, zIndex: 2, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              opacity: uiPlaying ? 0 : 1, transition: 'opacity 150ms ease',
            }}
          >
            <IconButton
              aria-label={uiPlaying ? 'Pause' : 'Play'}
              onClick={(e) => { e.stopPropagation(); togglePlay(); }}
              sx={{ width: 56, height: 56, color: '#fff', backgroundColor: 'rgba(15,23,42,0.55)', '&:hover': { backgroundColor: 'rgba(15,23,42,0.78)' } }}
            >
              {uiPlaying ? <PauseIcon sx={{ fontSize: 32 }} /> : <PlayArrowIcon sx={{ fontSize: 32 }} />}
            </IconButton>
          </Box>
        )}
        {!hasVideo && (
          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', textAlign: 'center', px: 2 }}>
            <Typography variant="body2">Add clips to the video track to preview your edit here.</Typography>
          </Box>
        )}
      </Box>

      {/* One <audio> per audio-layer clip. Hidden — the rAF loop drives them. */}
      {(timeline.audioTracks || []).flatMap((track) => (track.clips || []).map((c) => {
        const url = clipsById.get(c.sourceFileId)?.streamUrl;
        if (!url) return null;
        return (
          <audio
            key={c.id}
            src={url}
            preload="auto"
            ref={(el) => { if (el) audioEls.current.set(c.id, el); else audioEls.current.delete(c.id); }}
          />
        );
      }))}
    </Box>
  );
});

export default TimelinePreview;
